import React, { useState } from 'react';
import debounce from 'lodash/debounce';
import config from '../../config';
import { Alert, Button, FieldTextInputHighlighted, InterruptProcessModal } from '../../components';
import {
    composeValidators,
    emailRegExp,
    hyperlinkRegExp,
    noExternalContactDetails,
    phoneNumRegExp,
    required,
} from '../../util/validators';
import css from './StepSendInquiryForm.css';
import BlockSponsoredTransaction from './BlockSponsoredTransaction';
import { useSelector } from 'react-redux';
import { ensureListing, ensureUser } from '../../util/data';
import { checkIfListingIsAnonymous } from '../../util/anonym';
import BlockAnonymousListing from './BlockAnonymousListing';
import { datediff } from '../../util/time';
import { birthDateToDate } from '../../util/dates';

const { userTypeHorseowner } = config;

const mapper = {
    insufficient: 'failure',
    sufficient: 'warning',
    total: 'success',
};

const { allowedDomains } = config;

const regExpArray = [hyperlinkRegExp, emailRegExp, phoneNumRegExp, /www\./g].concat(
    allowedDomains.map(domain => new RegExp(`[\\d\\w]{1,}\\.${domain}`, 'gi'))
);

export default ({
    publicUserName,
    matchingScore,
    currentUser,
    publicUser,
    inProgress,
    onSubmit,
    onClose,
    disabled,
    values,
    form,
}) => {
    const [minAgeModalOpen, setMinAgeModalOpen] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const allListings = useSelector(s => s.marketplaceData.entities.listing);

    const { listingId } = values || {};
    const {
        attributes: {
            profile: {
                publicData: { userType, birthDate: currentUserBirthDate },
                protectedData: { representationListingId },
            },
        },
    } = currentUser;
    const {
        attributes: {
            profile: {
                publicData: { birthDate: publicUserBirthDate },
            },
        },
    } = ensureUser(publicUser);

    const currentUserIsOwner = userType === userTypeHorseowner;
    const ensuredListing = ensureListing(allListings[representationListingId || listingId]);
    const horseListing = ensureListing(allListings[listingId]);
    /** an owner selects an anonym listing to request a rider with */
    const isAnonymousListing = checkIfListingIsAnonymous(ensuredListing);
    /** to prevent multiple submit on quick tap */
    const debouncedSubmit = debounce(onSubmit, 1000, { leading: false });

    const birthDate = currentUserIsOwner ? publicUserBirthDate : currentUserBirthDate;
    const currentUserAge = birthDate && datediff(birthDateToDate(birthDate), new Date())?.years;
    const {
        attributes: {
            publicData: { minimumAge: listingMinimumAge },
        },
    } = horseListing;

    const showMinAgeModal =
        minAgeModalOpen &&
        currentUserAge &&
        listingMinimumAge &&
        currentUserAge < listingMinimumAge;

    if (showMinAgeModal) {
        const description = currentUserIsOwner
            ? `Dieses Profil erfüllt das von dir festgelegte Mindestalter nicht. Bist Du sicher, dass Du weiterfahren möchtest?`
            : ` Dein Profil erfüllt das Mindestalter nicht. Bist Du sicher, dass Du weiterfahren möchtest?`;
        return (
            <InterruptProcessModal
                isOpen
                onClose={() => onClose()}
                handleInterruption={() => {
                    setMinAgeModalOpen(false);
                }}
                scrollLayerClasses={css.ageNotificationScrollLayer}
            >
                <InterruptProcessModal.Icon />
                <InterruptProcessModal.Heading />
                <InterruptProcessModal.Description>{description}</InterruptProcessModal.Description>
                <InterruptProcessModal.Footer>
                    <InterruptProcessModal.Sidenote>
                        Mit dem Senden deiner Anfrage zeigst Du, dass Du <br /> diese Abweichung
                        bewusst in Kauf nimmst.
                    </InterruptProcessModal.Sidenote>
                    <InterruptProcessModal.Button>Weiterfahren</InterruptProcessModal.Button>
                </InterruptProcessModal.Footer>
            </InterruptProcessModal>
        );
    }

    return (
        <>
            <Alert
                header={`InquiryModal.alertHeading-${matchingScore}`}
                description={`InquiryModal.alertDesc-${matchingScore}`}
                closeAllowed={false}
                type={mapper[matchingScore]}
                rootClassName={css.alert}
            />
            <BlockSponsoredTransaction
                form={form}
                publicUserName={publicUserName}
                disabled={submitted}
            />
            {isAnonymousListing && (
                <BlockAnonymousListing
                    currentUserIsOwner={currentUserIsOwner}
                    publicUserName={publicUserName}
                    disabled={submitted}
                    form={form}
                />
            )}
            <aside>
                <FieldTextInputHighlighted
                    id="comment"
                    name="comment"
                    type="textarea"
                    placeholder="Nachricht"
                    disabled={submitted}
                    maxLength={1000}
                    field={values.comment || ''}
                    regExpArray={regExpArray}
                    validate={composeValidators(
                        required('Dieses Feld wird benötigt'),
                        noExternalContactDetails(
                            'Entferne externe Links, Telefonnummern und E-Mail-Adressen.'
                        )
                    )}
                />
            </aside>
            <footer>
                <p className={css.sidenote}>
                    {publicUserName} kann deine Anfrage innert 14 Tagen akzeptieren oder ablehnen.
                </p>
                <Button
                    type="button"
                    disabled={disabled || submitted}
                    inProgress={inProgress || submitted}
                    onClick={() => {
                        setSubmitted(true);
                        debouncedSubmit();
                    }}
                >
                    Anfrage senden
                </Button>
            </footer>
        </>
    );
};
